import Head from 'next/head';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Brain, FileText, BarChart, BookOpen, LogOut, ChevronRight, ExternalLink, Info, AlertTriangle, CheckCircle2, CalendarDays } from 'lucide-react';
import { format, isSameDay, startOfDay, subDays } from 'date-fns';
import { useAuth, useSupabaseClient } from '@/lib/supabase/AuthContext';
import { useUser as useAuthHelpersUser } from '@supabase/auth-helpers-react';
import { useEntitlements } from '@/components/auth/EntitlementGuards';
import {
    getLnatResources,
    getLnatMistakes,
    getLnatEssayTemplates,
    getLnatUniversityScoreReports,
    getUserLnatPracticeSessions,
    LnatResource,
    LnatMistake,
    LnatEssayTemplate,
    LnatUniversityScoreReport,
    LnatPracticeSession
} from '@/lib/lnat/service';
import type { MomentumAccuracyDatum } from '@/components/lnat/MomentumAccuracyChart';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import FeatureFeedbackLink from "@/components/feedback/FeatureFeedbackLink";
import { getSupabaseClient } from "@/lib/supabase/client";
import { LNAT_PRICE_LABEL, PRICING_CONFIG } from "@/config/pricing";

const MomentumAccuracyChart = dynamic(
    () => import('@/components/lnat/MomentumAccuracyChart'),
    { ssr: false }
);

const DAILY_GOAL_OPTIONS = [10, 20, 30, 45, 60] as const;
const DEFAULT_DAILY_GOAL = 20;

export default function LnatDashboard() {
    const router = useRouter();

    // Loading states
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<{
        resources: LnatResource[];
        mistakes: LnatMistake[];
        templates: LnatEssayTemplate[];
        scoreReports: LnatUniversityScoreReport[];
        sessions: LnatPracticeSession[];
    }>({
        resources: [],
        mistakes: [],
        templates: [],
        scoreReports: [],
        sessions: [],
    });

    // Modal state
    const [selectedCategory, setSelectedCategory] = useState<'resources' | 'mistakes' | 'templates' | 'scoreReports' | null>(null);

    const { hasLnatAccess, isMember, loading: entitlementsLoading } = useEntitlements();
    const { user, loading: authLoading } = useAuth();
    const authHelpersUser = useAuthHelpersUser();
    const effectiveUser = user ?? authHelpersUser ?? null;
    const effectiveAuthLoading = authLoading && !authHelpersUser;
    const supabase = useSupabaseClient();
    const [showPaywall, setShowPaywall] = useState(false);
    const [dailyGoalMinutes, setDailyGoalMinutes] = useState<number>(DEFAULT_DAILY_GOAL);

    const lnatHqModules = [
        {
            title: "Study Plan",
            route: "/lnat/study-plan",
            icon: <CalendarDays className="w-5 h-5" />,
            description: "Plan your preparation by phase and keep weekly targets measurable.",
            iconBg: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
        },
        {
            title: "Techniques",
            route: "/lnat/techniques",
            icon: <Brain className="w-5 h-5" />,
            description: "Section A/B decision frameworks for repeatable reasoning quality.",
            iconBg: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
        },
        {
            title: "Practice",
            route: "/lnat/practice",
            icon: <BookOpen className="w-5 h-5" />,
            description: "Timed drill routines mapped to LNAT resources and execution blocks.",
            iconBg: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        },
        {
            title: "AI Drills",
            route: "/lnat/drills",
            icon: <Brain className="w-5 h-5" />,
            description: "Timed drills + review loops to build speed, accuracy, and decision stamina.",
            iconBg: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
        },
        {
            title: "Mistakes",
            route: "/lnat/mistakes",
            icon: <AlertTriangle className="w-5 h-5" />,
            description: "Common traps dataset with direct “how to fix” correction workflows.",
            iconBg: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        },
        {
            title: "Essay Toolkit",
            route: "/lnat/essay",
            icon: <FileText className="w-5 h-5" />,
            description: "Templates + rubric-style self-check before final Section B submission.",
            iconBg: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        },
        {
            title: "Score Insights",
            route: "/lnat/scores",
            icon: <BarChart className="w-5 h-5" />,
            description: "Interpret score reports ethically with context-aware benchmark reading.",
            iconBg: "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300",
        },
    ];

    useEffect(() => {
        if (!effectiveUser?.id || typeof window === "undefined") {
            setDailyGoalMinutes(DEFAULT_DAILY_GOAL);
            return;
        }

        const storageKey = `lnat_daily_goal_minutes::${effectiveUser.id}`;
        const stored = window.localStorage.getItem(storageKey);
        const parsed = Number(stored);
        if (DAILY_GOAL_OPTIONS.includes(parsed as (typeof DAILY_GOAL_OPTIONS)[number])) {
            setDailyGoalMinutes(parsed);
            return;
        }

        setDailyGoalMinutes(DEFAULT_DAILY_GOAL);
        window.localStorage.setItem(storageKey, String(DEFAULT_DAILY_GOAL));
    }, [effectiveUser?.id]);

    useEffect(() => {
        if (!effectiveUser?.id || typeof window === "undefined") {
            return;
        }

        const storageKey = `lnat_daily_goal_minutes::${effectiveUser.id}`;
        window.localStorage.setItem(storageKey, String(dailyGoalMinutes));
    }, [dailyGoalMinutes, effectiveUser?.id]);

    useEffect(() => {
        if (effectiveAuthLoading) {
            return;
        }

        if (!effectiveUser) {
            router.replace('/lnat/login');
            return;
        }

        loadData();
    }, [effectiveUser, router, effectiveAuthLoading]);

    const loadData = async () => {
        if (!effectiveUser?.id) {
            return;
        }

        setLoading(true);
        try {
            const [resources, mistakes, templates, scoreReports, sessions] = await Promise.all([
                getLnatResources(),
                getLnatMistakes(),
                getLnatEssayTemplates(),
                getLnatUniversityScoreReports(),
                getUserLnatPracticeSessions(effectiveUser.id),
            ]);
            setData({ resources, mistakes, templates, scoreReports, sessions });
        } catch (error) {
            console.error("Error loading LNAT data:", error);
        } finally {
            setLoading(false);
        }
    };

    const sessionDateKeys = useMemo(() => {
        return new Set(
            data.sessions.map((session) =>
                format(startOfDay(new Date(session.created_at)), "yyyy-MM-dd")
            )
        );
    }, [data.sessions]);

    const todayMinutes = useMemo(() => {
        const now = new Date();
        return data.sessions.reduce((sum, session) => {
            const createdAt = new Date(session.created_at);
            if (!isSameDay(createdAt, now)) {
                return sum;
            }
            return sum + (session.duration_minutes ?? 0);
        }, 0);
    }, [data.sessions]);

    const streak = useMemo(() => {
        if (data.sessions.length === 0) {
            return 0;
        }

        let currentDay = startOfDay(new Date());
        let nextStreak = 0;

        while (sessionDateKeys.has(format(currentDay, "yyyy-MM-dd"))) {
            nextStreak += 1;
            currentDay = subDays(currentDay, 1);
        }

        return nextStreak;
    }, [data.sessions.length, sessionDateKeys]);

    const last7DaysTrend = useMemo<MomentumAccuracyDatum[]>(() => {
        const todayDate = startOfDay(new Date());

        return Array.from({ length: 7 }, (_, index) => {
            const day = subDays(todayDate, 6 - index);
            let correctTotal = 0;
            let questionTotal = 0;

            data.sessions.forEach((session) => {
                const createdAt = new Date(session.created_at);
                if (!isSameDay(createdAt, day)) {
                    return;
                }

                correctTotal += session.correct_count ?? 0;
                questionTotal += session.question_count ?? 0;
            });

            return {
                label: format(day, "EEE"),
                fullDateLabel: format(day, "MMM d"),
                accuracyPercent:
                    questionTotal > 0
                        ? Number(((correctTotal / questionTotal) * 100).toFixed(1))
                        : null,
            };
        });
    }, [data.sessions]);

    const hasMomentumHistory = data.sessions.length > 0;
    const todayProgress = dailyGoalMinutes > 0 ? Math.min(todayMinutes / dailyGoalMinutes, 1) : 0;
    const todayProgressPercent = Math.round(todayProgress * 100);

    const handleSignOut = async () => {
        await supabase.auth.signOut({ scope: 'local' });
        router.push('/');
    };

    if (effectiveAuthLoading) return null;

    return (
        <>
            <Head>
                <title>LNAT Mentor | Dashboard</title>
            </Head>
            <div className="min-h-screen bg-gray-50 dark:bg-[#0B1412] font-sans transition-colors duration-300">
                {/* LNAT Header */}
                <header className="bg-white dark:bg-[#0B1412]/80 backdrop-blur-md border-b border-gray-200 dark:border-white/10 sticky top-0 z-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                            <span className="font-bold text-gray-900 dark:text-white tracking-tight">LNAT Mentor</span>
                            <span className="px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-[10px] font-bold uppercase tracking-wider">Active</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <Button variant="ghost" size="sm" onClick={handleSignOut} className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
                                <LogOut className="w-4 h-4 mr-2" />
                                Sign Out
                            </Button>
                        </div>
                    </div>
                </header>

                <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <div className="flex-1">
                            <div className="flex items-center gap-3">
                                <h1 className="text-4xl font-black text-gray-900 dark:text-white mb-3">LNAT HQ</h1>
                                <FeatureFeedbackLink featureContext="LNAT Tools" />
                            </div>
                            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl leading-relaxed">
                                A complete LNAT study system: plan → techniques → practice → mistakes → essay → score insights.
                                <br /><span className="text-sm text-purple-600 dark:text-purple-400 font-medium mt-2 block flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4" /> University score table withdrawn pending source review
                                </span>
                            </p>
                        </div>

                        {!hasLnatAccess && (
                            <div className="shrink-0">
                                <Button
                                    className="bg-purple-600 hover:bg-purple-700 text-white font-black px-8 py-6 rounded-2xl shadow-xl shadow-purple-500/20 text-lg flex flex-col items-center gap-0 h-auto"
                                    onClick={() => setShowPaywall(true)}
                                >
                                    <span className="text-xs font-bold uppercase tracking-widest opacity-80 mb-1">Trial Expired</span>
                                    <span>Unlock Full Access</span>
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="mb-5">
                        <h2 className="text-2xl font-black text-gray-900 dark:text-white">Momentum</h2>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                            Track consistency, today&apos;s practice target, and the last 7 days of scoring accuracy.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3 mb-12">
                        <section className="rounded-3xl border border-gray-200 bg-white p-6 dark:border-white/5 dark:bg-[#121D1A]">
                            <p className="text-xs font-black uppercase tracking-widest text-purple-600 dark:text-purple-300">
                                Streak
                            </p>
                            {loading ? (
                                <div className="mt-4 h-20 animate-pulse rounded-2xl bg-gray-100 dark:bg-white/5" />
                            ) : (
                                <>
                                    <div className="mt-4 flex items-end gap-3">
                                        <span className="text-5xl font-black text-gray-900 dark:text-white">
                                            {streak}
                                        </span>
                                        <span className="pb-1 text-sm font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                            days
                                        </span>
                                    </div>
                                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                                        {hasMomentumHistory
                                            ? streak > 0
                                                ? "You have logged practice on consecutive days through today."
                                                : "No session saved today yet. Save one today to restart your streak."
                                            : "No sessions yet. Save your first practice block to start a streak."}
                                    </p>
                                </>
                            )}
                        </section>

                        <section className="rounded-3xl border border-gray-200 bg-white p-6 dark:border-white/5 dark:bg-[#121D1A]">
                            <p className="text-xs font-black uppercase tracking-widest text-purple-600 dark:text-purple-300">
                                Daily Goal
                            </p>
                            {loading ? (
                                <div className="mt-4 h-20 animate-pulse rounded-2xl bg-gray-100 dark:bg-white/5" />
                            ) : (
                                <>
                                    <label className="mt-4 block text-sm font-bold text-gray-700 dark:text-gray-200">
                                        Goal minutes
                                        <select
                                            value={dailyGoalMinutes}
                                            onChange={(event) => setDailyGoalMinutes(Number(event.target.value))}
                                            className="mt-2 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900 dark:border-white/10 dark:bg-white/5 dark:text-white"
                                        >
                                            {DAILY_GOAL_OPTIONS.map((goal) => (
                                                <option key={goal} value={goal}>
                                                    {goal} min
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <div className="mt-4">
                                        <div className="mb-2 flex items-center justify-between text-sm font-semibold text-gray-600 dark:text-gray-300">
                                            <span>{todayMinutes} / {dailyGoalMinutes} min</span>
                                            <span>{todayProgressPercent}%</span>
                                        </div>
                                        <div className="h-3 rounded-full bg-gray-100 dark:bg-white/10 overflow-hidden">
                                            <div
                                                className="h-full rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 transition-all duration-300"
                                                style={{ width: `${todayProgressPercent}%` }}
                                            />
                                        </div>
                                    </div>
                                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                                        {hasMomentumHistory
                                            ? "Today's logged minutes update automatically from saved sessions."
                                            : "Pick a target now. Your progress bar will fill once you start saving sessions."}
                                    </p>
                                </>
                            )}
                        </section>

                        <section className="rounded-3xl border border-gray-200 bg-white p-6 dark:border-white/5 dark:bg-[#121D1A] md:col-span-2 xl:col-span-1">
                            <p className="text-xs font-black uppercase tracking-widest text-purple-600 dark:text-purple-300">
                                7-Day Accuracy
                            </p>
                            {loading ? (
                                <div className="mt-4 h-64 animate-pulse rounded-2xl bg-gray-100 dark:bg-white/5" />
                            ) : hasMomentumHistory ? (
                                <>
                                    <div className="mt-4 rounded-2xl border border-gray-100 bg-gray-50 p-3 dark:border-white/5 dark:bg-[#0B1412]">
                                        <MomentumAccuracyChart data={last7DaysTrend} />
                                    </div>
                                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                                        Accuracy is based on saved correct and question totals by day. Missing days stay empty.
                                    </p>
                                </>
                            ) : (
                                <div className="mt-4 flex h-64 items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center dark:border-white/10 dark:bg-white/5">
                                    <div>
                                        <p className="text-sm font-bold text-gray-900 dark:text-white">No accuracy data yet</p>
                                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                                            Save a practice session to unlock your 7-day trend line.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </section>
                    </div>

                    <div className="mb-5">
                        <h2 className="text-2xl font-black text-gray-900 dark:text-white">Study Modules</h2>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                            Follow the full workflow without dead ends. Every module links forward to the next step.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                        {lnatHqModules.map((moduleItem) => (
                            <DashboardCard
                                key={moduleItem.route}
                                icon={moduleItem.icon}
                                title={moduleItem.title}
                                description={moduleItem.description}
                                iconBg={moduleItem.iconBg}
                                onClick={() => router.push(moduleItem.route)}
                                loading={false}
                            />
                        ))}
                    </div>

                    <div className="mb-5">
                        <h2 className="text-2xl font-black text-gray-900 dark:text-white">Reference Library</h2>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                            Dataset cards for resources, templates, mistake entries, and score reports.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                        <DashboardCard
                            icon={<BookOpen className="w-5 h-5" />}
                            title="Official Resources"
                            description={`${data.resources.length} practice materials & guides. Check current provider information.`}
                            iconBg="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                            onClick={() => setSelectedCategory('resources')}
                            loading={loading}
                        />

                        <DashboardCard
                            icon={<AlertTriangle className="w-5 h-5" />}
                            title="Common Mistakes"
                            description={`${data.mistakes.length} tactical pitfalls to avoid in Section A & B.`}
                            iconBg="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                            onClick={() => setSelectedCategory('mistakes')}
                            loading={loading}
                        />

                        <DashboardCard
                            icon={<FileText className="w-5 h-5" />}
                            title="Essay Templates"
                            description={`${data.templates.length} structural frameworks for Section B.`}
                            iconBg="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                            onClick={() => setSelectedCategory('templates')}
                            loading={loading}
                        />

                        <DashboardCard
                            icon={<BarChart className="w-5 h-5" />}
                            title="Score Reports"
                            description="Score table under review. Check official admissions sources."
                            iconBg="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                            onClick={() => setSelectedCategory('scoreReports')}
                            loading={loading}
                        />
                    </div>

                    <div className="bg-gradient-to-br from-gray-900 via-purple-900 to-black rounded-3xl p-10 text-white relative overflow-hidden shadow-2xl">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl -mr-20 -mt-20"></div>
                        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                            <div className="text-left max-w-xl">
                                <h2 className="text-3xl font-bold mb-4">Integrate with Durmah AI</h2>
                                <p className="text-gray-300 mb-0 leading-relaxed text-lg">
                                    Durmah can now reference these datasets directly. Ask about specific university scores, essay structures, or common pitfalls during your study sessions.
                                </p>
                            </div>
                            <Button className="bg-white text-gray-900 hover:bg-gray-100 font-bold px-10 py-6 rounded-2xl text-lg shadow-xl shrink-0" onClick={() => router.push('/dashboard')}>
                                Open Durmah
                            </Button>
                        </div>
                    </div>
                </main>

                {/* Modals for Data Views */}
                <DataModal
                    isOpen={selectedCategory !== null}
                    onClose={() => setSelectedCategory(null)}
                    title={
                        selectedCategory === 'resources' ? 'Official Resources' :
                            selectedCategory === 'mistakes' ? 'Common Mistakes' :
                                selectedCategory === 'templates' ? 'Essay Templates' :
                                    'University Score Reports'
                    }
                >
                    {selectedCategory === 'resources' && <ResourcesList items={data.resources} />}
                    {selectedCategory === 'mistakes' && <MistakesList items={data.mistakes} />}
                    {selectedCategory === 'templates' && <TemplatesList items={data.templates} />}
                    {selectedCategory === 'scoreReports' && <ScoreReportsList items={data.scoreReports} />}
                </DataModal>

                {/* Purchase Modal */}
                <Dialog open={showPaywall} onOpenChange={setShowPaywall}>
                    <DialogContent className="max-w-md p-0 border-0 overflow-hidden rounded-[2rem] dark:bg-[#0B1412]">
                        <div className="bg-purple-600 p-8 text-white text-center">
                            <Brain className="w-12 h-12 mx-auto mb-4" />
                            <h2 className="text-2xl font-black mb-2">Unlock LNAT Mentor</h2>
                            <p className="text-purple-100 text-sm">Get three months of access to our LNAT study hub.</p>
                        </div>
                        <div className="p-8 space-y-6">
                            <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-2xl border border-gray-100 dark:border-white/5">
                                <div className="flex justify-between items-baseline mb-2">
                                    <span className="text-3xl font-black">{LNAT_PRICE_LABEL}</span>
                                    <span className="text-gray-400 text-sm font-bold uppercase">One-off</span>
                                </div>
                                <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                                    {PRICING_CONFIG.LNAT.duration} access, no subscription
                                </div>
                            </div>

                            <ul className="space-y-4">
                                <li className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                                    <CheckCircle2 className="w-5 h-5 text-purple-500" />
                                    <span>All resources, templates & reports</span>
                                </li>
                                <li className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                                    <CheckCircle2 className="w-5 h-5 text-purple-500" />
                                    <span>Durmah AI Intelligence Integration</span>
                                </li>
                                <li className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                                    <CheckCircle2 className="w-5 h-5 text-purple-500" />
                                    <span>2026 Admissions Data Sync</span>
                                </li>
                            </ul>

                            <Button
                                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-black py-4 rounded-xl shadow-lg shadow-purple-500/20"
                                onClick={async () => {
                                    // Canonical payload shape + Bearer auth (checkout API reads Authorization header).
                                    const { data: { session } } = await getSupabaseClient().auth.getSession();
                                    const res = await fetch('/api/stripe/checkout', {
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json',
                                            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
                                        },
                                        body: JSON.stringify({ product: 'lnat', interval: null })
                                    });
                                    const { url } = await res.json();
                                    if (url) window.location.href = url;
                                }}
                            >
                                Get Access Now
                            </Button>

                            {!isMember && (
                                <p className="text-center text-[10px] text-gray-400 uppercase font-bold tracking-widest cursor-pointer hover:text-indigo-500 transition-colors" onClick={() => router.push('/pricing')}>
                                    Full Pricing Details
                                </p>
                            )}
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </>
    );
}

function DashboardCard({ icon, title, description, iconBg, onClick, loading }: any) {
    return (
        <div
            onClick={onClick}
            className="group bg-white dark:bg-[#121D1A] p-7 rounded-3xl border border-gray-200 dark:border-white/5 hover:border-purple-300 dark:hover:border-purple-500/50 hover:shadow-2xl hover:shadow-purple-500/10 transition-all cursor-pointer relative"
        >
            <div className={`w-12 h-12 rounded-2xl ${iconBg} flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform`}>
                {icon}
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{title}</h3>
            {loading ? (
                <div className="h-4 bg-gray-100 dark:bg-white/5 rounded w-2/3 animate-pulse"></div>
            ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors leading-relaxed">
                    {description}
                </p>
            )}
            <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity translate-x-1 group-hover:translate-x-0">
                <ChevronRight className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
        </div>
    );
}

function DataModal({ isOpen, onClose, title, children }: any) {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col p-0 border-0 dark:bg-[#0B1412] dark:text-white">
                <DialogHeader className="p-8 border-b border-gray-100 dark:border-white/5 shrink-0">
                    <DialogTitle className="text-2xl font-black">{title}</DialogTitle>
                    <DialogDescription className="dark:text-gray-400">LNAT study resources. Check current provider and admissions sources.</DialogDescription>
                </DialogHeader>
                <div className="overflow-y-auto p-8 flex-1 custom-scrollbar">
                    {children}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function ResourcesList({ items }: { items: LnatResource[] }) {
    return (
        <div className="space-y-6">
            {items.map(item => (
                <div key={item.id} className="p-6 rounded-2xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/5 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                        <h4 className="text-lg font-bold text-gray-900 dark:text-white">{item.resource_name}</h4>
                        <span className="text-[10px] uppercase font-black px-2 py-1 rounded-md bg-white dark:bg-white/10 shadow-sm">{item.resource_type}</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">{item.description}</p>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-gray-500 font-medium font-mono">
                        <div className="flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Stage: {item.recommended_use_stage}</div>
                        <div className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Verified: {item.last_verified_date}</div>
                        {item.access_url && (
                            <a href={item.access_url} target="_blank" rel="noopener noreferrer" className="ml-auto text-purple-600 dark:text-purple-400 hover:underline flex items-center gap-1 font-sans">
                                Visit Resource <ExternalLink className="w-3 h-3" />
                            </a>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

function MistakesList({ items }: { items: LnatMistake[] }) {
    return (
        <div className="space-y-6">
            {items.map(item => (
                <div key={item.id} className="p-6 rounded-2xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/5">
                    <div className="flex justify-between items-start mb-3">
                        <div>
                            <span className="text-[10px] font-black bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-2 py-1 rounded-md mb-2 inline-block">SECTION {item.section} • {item.skill_tag}</span>
                            <h4 className="text-lg font-bold text-gray-900 dark:text-white">{item.mistake_title}</h4>
                        </div>
                        <span className="text-[10px] uppercase font-mono bg-white dark:bg-white/10 px-2 py-1 rounded shadow-sm">{item.difficulty_level}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                        <div>
                            <h5 className="text-xs font-black uppercase tracking-wider text-gray-400 mb-2">The Pitfall</h5>
                            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{item.mistake_description}</p>
                        </div>
                        <div>
                            <h5 className="text-xs font-black uppercase tracking-wider text-emerald-500 mb-2">Fix Strategy</h5>
                            <p className="text-sm text-emerald-700 dark:text-emerald-400 font-medium leading-relaxed">{item.correction_strategy}</p>
                        </div>
                    </div>
                    <div className="mt-6 pt-4 border-t border-gray-200 dark:border-white/5 flex items-center justify-between">
                        <div className="text-[10px] text-gray-400 font-mono">Verified: {item.last_verified_date}</div>
                        {item.citation_url && <a href={item.citation_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-purple-600 transition-colors"><ExternalLink className="w-3 h-3" /></a>}
                    </div>
                </div>
            ))}
        </div>
    );
}

function TemplatesList({ items }: { items: LnatEssayTemplate[] }) {
    return (
        <div className="space-y-6">
            {items.map(item => (
                <div key={item.id} className="p-6 rounded-2xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/5">
                    <div className="flex justify-between items-center mb-6">
                        <h4 className="text-xl font-bold text-gray-900 dark:text-white">{item.template_name}</h4>
                        <div className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 px-3 py-1 rounded-full text-xs font-bold">{item.time_allocation_minutes} Min</div>
                    </div>

                    <div className="space-y-6">
                        <section>
                            <h5 className="text-xs font-black uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-2"><Info className="w-3 h-3" /> Use Case</h5>
                            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{item.use_case}</p>
                        </section>

                        <section>
                            <h5 className="text-xs font-black uppercase tracking-wider text-purple-500 mb-2">Thesis Guidance</h5>
                            <p className="text-sm text-gray-900 dark:text-white font-medium bg-purple-50 dark:bg-purple-900/10 p-4 rounded-xl italic">"{item.thesis_guidance}"</p>
                        </section>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <section>
                                <h5 className="text-xs font-black uppercase tracking-wider text-gray-400 mb-2">Blueprint</h5>
                                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap">{item.paragraph_blueprint}</p>
                            </section>
                            <section>
                                <h5 className="text-xs font-black uppercase tracking-wider text-amber-500 mb-2">Counter-Argument</h5>
                                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap">{item.counterargument_pattern}</p>
                            </section>
                        </div>
                    </div>

                    <div className="mt-8 pt-4 border-t border-gray-200 dark:border-white/5 flex items-center gap-4">
                        <span className="text-[10px] text-gray-400 font-mono">Last Verified: {item.last_verified_date}</span>
                        {item.citation_url && <a href={item.citation_url} target="_blank" rel="noopener noreferrer" className="ml-auto text-gray-400 hover:text-purple-600"><ExternalLink className="w-3 h-3" /></a>}
                    </div>
                </div>
            ))}
        </div>
    );
}

function ScoreReportsList({ items }: { items: LnatUniversityScoreReport[] }) {
    return (
        <div className="space-y-6">
            {items.map(item => (
                <div key={item.id} className="p-6 rounded-2xl bg-white dark:bg-white/5 shadow-sm border border-gray-100 dark:border-white/5 relative overflow-hidden">
                    {item.score_type !== 'official' && (
                        <div className="absolute top-0 right-0 left-0 bg-amber-500 text-white text-[9px] font-black uppercase py-1 text-center tracking-widest shadow-sm">
                            Indicative / Reported Data Only
                        </div>
                    )}
                    <div className={`flex justify-between items-start mb-4 ${item.score_type !== 'official' ? 'mt-4' : ''}`}>
                        <div>
                            <h4 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">{item.university_name}</h4>
                            <p className="text-xs text-gray-500 dark:text-gray-500 font-medium uppercase tracking-wider mt-1">{item.country} • {item.data_year} Cycle</p>
                        </div>
                        <span className={`text-[10px] font-black px-2 py-1 rounded-md ${item.score_type === 'official' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400'
                            }`}>
                            {item.score_type}
                        </span>
                    </div>

                    <div className="p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/5 mb-4">
                        <h5 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Scores & Stats</h5>
                        <p className="text-sm text-gray-900 dark:text-white font-medium leading-relaxed">{item.score_range_or_statement}</p>
                    </div>

                    <div className="space-y-3 mb-6">
                        <div className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                            <span className="font-bold text-gray-900 dark:text-white uppercase text-[9px] mr-1">Usage:</span> {item.admissions_usage_note}
                        </div>
                    </div>

                    <div className="pt-4 border-t border-gray-100 dark:border-white/5 flex items-center justify-between text-[10px] text-gray-400 font-mono">
                        <div>Verified: {item.last_verified_date}</div>
                        {item.citation_url && (
                            <a href={item.citation_url} target="_blank" rel="noopener noreferrer" className="text-purple-600 dark:text-purple-400 hover:underline flex items-center gap-1 font-sans font-bold uppercase text-[9px]">
                                Source Doc <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                        )}
                    </div>

                    {item.disclaimer_required && (
                        <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 text-[10px] text-red-700 dark:text-red-400 leading-normal flex gap-2">
                            <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                            <span>This data is indicative and subject to annual fluctuation. Always cross-reference with final university admissions statements.</span>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}
